import dayjs from 'dayjs';
import 'dayjs/locale/fr';

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import car from '../assets/car (2).png';
import heroCar from '../assets/hero-clio.jpg';
import AutocompleteMapInput from '../components/AutocompleteMapInput';
import BodySection1 from '../components/BodySection1';
import HeaderSection2 from '../components/HeaderSection2';
import HeaderSection3 from '../components/HeaderSection3';
import McDateRangePicker from '../components/McDateRangePicker';
import RecommendationsModal from '../components/RecommendationsModal';
import env from '../config/env.js';
import { useAxios } from '../utils/hook/useAxios';

import { useUser } from '../utils/hook/UserContext';

import './Home.css';
import './HomeHero.css'; // <-- le nouveau CSS (après ./Home.css)

dayjs.locale('fr');

function readLocalStorageJson(key, fallback) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

function Home() {
  const [locationCenter, setLocationCenter] = useState([0, 0]);
  const [locationPlaceName, setLocationPlaceName] = useState('');
  const [mobileSearchPanel, setMobileSearchPanel] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const AxiosRequest = useAxios();
  const [activeModal, setActiveModal] = useState(null);
  const openModal = (modal) => setActiveModal(modal);
  const closeModal = () => setActiveModal(null);

  const parseDate = (v) => (v ? new Date(v) : null);

  // ...dans ton composant :
  const [showReco, setShowReco] = useState(false);
  const [params] = useSearchParams();

  const start = useMemo(() => parseDate(params.get('start')), [params]);
  const end = useMemo(() => parseDate(params.get('end')), [params]);
  const lat = params.get('lat') ? Number(params.get('lat')) : undefined;
  const lng = params.get('lng') ? Number(params.get('lng')) : undefined;

  const [selectedVoitures, setSelectedVoitures] = useState(() =>
    readLocalStorageJson('selectedVoitures', [])
  );
  const [personnalisedMessage, setPersonnalisedMessage] = useState('');

  const { user, isAuthenticated } = useUser();

  useEffect(() => {
    localStorage.setItem('mode', 'guest');
  }, [user]);

  const handleUseMyPosition = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setLocationCenter([pos.coords.longitude, pos.coords.latitude]);
      setLocationPlaceName('Ma position');
    });
  };

  const handleLocationSelection2 = (locationAddress) => {
    setLocationCenter(locationAddress.location);
    setLocationPlaceName(locationAddress.name);
  };

  const today = dayjs();
  const startDateInit = today.add(3, 'day');
  const endDateInit = startDateInit.add(5, 'day');

  const [selectedDates, setSelectedDates] = useState([startDateInit, endDateInit]);

  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');

  const generateHourOptions = () => {
    const opts = [];

    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const t = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

        opts.push(
          <option key={t} value={t}>
            {t}
          </option>
        );
      }
    }

    return opts;
  };

  const navigate = useNavigate();
  const redirectToSignin = () => navigate('/signin');

  async function handleReserver() {
    if (!isAuthenticated) {
      closeModal();
      redirectToSignin();
      return;
    }

    if (selectedVoitures.length === 0) {
      toast.error('Aucune voiture sélectionnée.');
      return;
    }

    try {
      localStorage.setItem('personnalisedMessage', personnalisedMessage || '');

      let successCount = 0;

      for (let j = 0; j < selectedVoitures.length; j += 1) {
        const voiture = selectedVoitures[j];

        try {
          // Le backend détermine driverInviteId depuis req.user.id
          const response = await AxiosRequest.post(`/reservations`, {
            driverHoteId: voiture.driverId,
            vehiculeId: voiture.id_de_la_voiture,
            startDate: voiture.dateEntree,
            endDate: voiture.dateSortie,
            vehiculeAnnonceId: voiture.vehiculeAnnonceId,
          });

          try {
            await AxiosRequest.post(`/conversations`, {
              authorDriverRole: 1,
              driversId: voiture.driverId,
              sentTime: new Date(),
              reservationId: response.data.reservationId,
              message: personnalisedMessage || 'Nouvelle demande de réservation',
            });

            // Corrigé : utilisait axios (sans baseURL ni token) au lieu de AxiosRequest
            await AxiosRequest.post(`/reservationsGain`, {
              reservationId: response.data.reservationId,
              amount: Number(voiture.prix_par_jour) * Number(voiture.nombre_de_jours),
              type: '0',
            });
          } catch (innerError) {
            console.error('Erreur conversation/gain :', innerError);
          }

          successCount += 1;
        } catch (error) {
          const msg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Une erreur est survenue. Veuillez réessayer.';

          if (error?.response?.status === 409) {
            toast.error(`🚫 ${msg}`, { autoClose: 6000 });
          } else {
            toast.error(msg);
          }
        }
      }

      if (successCount > 0) {
        localStorage.removeItem('selectedVoitures');

        setSelectedVoitures([]);
        setPersonnalisedMessage('');

        // On remplace la modale de confirmation par la modale de succès
        openModal('success');
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi des demandes :", error);

      toast.error('Une erreur est survenue. Veuillez réessayer.');
    }
  }

  const handleSuccessOk = () => {
    closeModal();
    window.location.href = `${env.apiUrl}/guest/listing/reservations`;
  };

  const handleSearch = () => {
    window.location.href =
      '/search?address=' +
      encodeURIComponent(locationPlaceName) +
      '&center=' +
      locationCenter +
      '&address_source=mapbox' +
      '&longitude=' +
      locationCenter[0] +
      '&latitude=' +
      locationCenter[1] +
      '&start=' +
      new Date(selectedDates[0]) +
      '&end=' +
      new Date(selectedDates[1]) +
      '&startTime=' +
      startTime +
      '&endTime=' +
      endTime;
  };

  const handleDateChange = (newDates) => {
    setSelectedDates(newDates);
  };

  const nombreJours = (() => {
    if (!selectedDates[0] || !selectedDates[1]) return 0;

    const diff = selectedDates[1].diff(selectedDates[0], 'day');

    return diff > 0 ? diff : 0;
  })();

  const startLabel = selectedDates[0]
    ? selectedDates[0].format('ddd D MMM') + ' ' + startTime
    : null;

  const endLabel = selectedDates[1] ? selectedDates[1].format('ddd D MMM') + ' ' + endTime : null;

  const PRIX_MIN = 25;
  const PRIX_MOY = 55;

  const prixEstimeMin = nombreJours * PRIX_MIN;
  const prixEstimeMoy = nombreJours * PRIX_MOY;

  return (
    <main className="mc-home">
      {/* ============================================================
    RECHERCHE MOBILE — VERSION COMPACTE
    Visible uniquement sur mobile
============================================================ */}

      {/* =========================================================
          HERO
      ========================================================= */}
      <section className="mv-hero">
        <div className="mv-hero-inner">
          {/* ================= COLONNE GAUCHE ================= */}
          <div className="mv-left">
            <div className="mv-ai-row">
              <span className="mv-ai-pill">
                <i className="bi bi-stars" /> Propulsé par l'IA
              </span>
              <span className="mv-ai-text">
                Des recommandations personnalisées pour votre trajet idéal
              </span>
            </div>

            <h1 className="mv-title">
              Louez une voiture <em>en quelques clics</em>
            </h1>

            <p className="mv-subtitle">
              Simple, rapide et sécurisé. Trouvez le véhicule qui vous correspond et partez l'esprit
              tranquille.
            </p>

            <div className="mv-features">
              {[
                { icon: 'bi-lightning-charge-fill', t: 'Réservation', s: 'en 2 minutes' },
                { icon: 'bi-shield-check', t: 'Véhicules vérifiés', s: 'et assurés' },
                { icon: 'bi-headset', t: 'Assistance 24/7', s: 'sur la route' },
              ].map((f) => (
                <div className="mv-feature" key={f.t}>
                  <span className="mv-feature-icon">
                    <i className={`bi ${f.icon}`} />
                  </span>
                  <span>
                    <strong>{f.t}</strong>
                    <small>{f.s}</small>
                  </span>
                </div>
              ))}
            </div>

            {/* ---------- CARTE DE RECHERCHE ---------- */}
            <div className="mv-search">
              <div className="mv-reco">
                <span className="mv-reco-icon">
                  <i className="bi bi-stars" />
                </span>
                <div className="mv-reco-text">
                  <strong>
                    L'IA vous recommande <b>le meilleur choix</b>
                  </strong>
                  <small>Basé sur vos préférences, votre historique et la disponibilité.</small>
                </div>

                <button className="mv-reco-btn" onClick={() => setShowReco(true)}>
                  <i className="bi bi-stars" /> Voir mes recommandations
                </button>
                {showReco && (
                  <RecommendationsModal
                    onClose={() => setShowReco(false)}
                    onOpenModal={openModal}
                    start={start}
                    end={end}
                    lat={lat}
                    lng={lng}
                  />
                )}
              </div>

              <>
                <AutocompleteMapInput handleSelectLocation={handleLocationSelection2} />
              </>

              {/* Dates (ton composant existant : on garde la logique) */}
              <div className="mv-dates mc-date-picker" style={{ marginTop: '0.5rem' }}>
                <McDateRangePicker value={selectedDates} onChange={handleDateChange} />
              </div>

              <div className="mv-times">
                <div className="mv-box">
                  <span className="mv-box-icon mv-plain">
                    <i className="bi bi-clock" />
                  </span>
                  <div className="mv-box-body">
                    <label>Heure de début</label>
                    <select value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                      {generateHourOptions()}
                    </select>
                  </div>
                </div>

                <div className="mv-box">
                  <span className="mv-box-icon mv-plain">
                    <i className="bi bi-clock" />
                  </span>
                  <div className="mv-box-body">
                    <label>Heure de fin</label>
                    <select value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                      {generateHourOptions()}
                    </select>
                  </div>
                </div>
              </div>

              {nombreJours > 0 && (
                <div className="mv-summary">
                  <i className="bi bi-calendar-check" />
                  <strong>
                    {nombreJours} jour{nombreJours > 1 ? 's' : ''}
                  </strong>
                  <span className="mv-dot" />
                  <span>
                    {prixEstimeMin} – {prixEstimeMoy} € estimé
                  </span>
                  <i className="bi bi-info-circle mv-info" />
                  <span className="mv-best">
                    <i className="bi bi-leaf" /> Meilleurs tarifs garantis
                  </span>
                </div>
              )}

              <button className="mv-cta" onClick={handleSearch}>
                <i className="bi bi-search" /> Rechercher une voiture{' '}
                <i className="bi bi-arrow-right" />
              </button>
            </div>

            {/* ---------- BANDEAU DE CONFIANCE ---------- */}
            <div className="mv-trust">
              {[
                { icon: 'bi-shield-check', c: 'teal', t: 'Paiement sécurisé', s: '100% protégé' },
                { icon: 'bi-star-fill', c: 'orange', t: 'Avis clients', s: '4.8/5 (2 500+)' },
                { icon: 'bi-headset', c: 'teal', t: 'Service client', s: "Réactif et à l'écoute" },
                {
                  icon: 'bi-leaf',
                  c: 'green',
                  t: 'Voyage responsable',
                  s: 'Des options plus vertes',
                },
              ].map((x) => (
                <div className="mv-trust-item" key={x.t}>
                  <span className={`mv-trust-icon ${x.c}`}>
                    <i className={`bi ${x.icon}`} />
                  </span>
                  <span>
                    <strong>{x.t}</strong>
                    <small>{x.s}</small>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ================= COLONNE DROITE (VISUEL) ================= */}
          <div className="mv-visual" aria-hidden="false">
            <div className="mv-teal-swoosh" />
            <div className="mv-photo">
              <img src={heroCar} alt="Renault Clio rouge disponible à la location" />
            </div>
            <div className="mv-orange-shape" />

            <p className="mv-script mv-script-top">
              Votre prochaine destination commence ici !
              <svg viewBox="0 0 90 60" className="mv-arrow">
                <path
                  d="M84 4 C 70 40, 40 52, 8 46 M8 46 l14 -10 M8 46 l16 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </p>

            {/* Carte véhicule */}
            <article className="mv-car-card">
              <div className="mv-car-card-img">
                <img src={heroCar} alt="" />
                <span className="mv-badge">
                  <i className="bi bi-stars" /> Recommandé par l'IA
                </span>
                <button type="button" className="mv-heart" aria-label="Ajouter aux favoris">
                  <i className="bi bi-heart" />
                </button>
              </div>
              <div className="mv-car-card-body">
                <h3>Renault Clio</h3>
                <ul>
                  <li>
                    <i className="bi bi-person" /> 5 places
                  </li>
                  <li>
                    <i className="bi bi-gear" /> Manuelle
                  </li>
                  <li>
                    <i className="bi bi-fuel-pump" /> Essence
                  </li>
                </ul>
                <div className="mv-car-card-price">
                  <div>
                    <small>À partir de</small>
                    <strong>
                      25 € <span>/ jour</span>
                    </strong>
                  </div>
                  <Link to="/search" className="mv-round-btn" aria-label="Voir l'offre">
                    <i className="bi bi-arrow-right" />
                  </Link>
                </div>
              </div>
            </article>

            {/* Téléphone (construit en CSS, sans image de main) */}
            <div className="mv-phone">
              <div className="mv-phone-screen">
                <div className="mv-phone-top">
                  <b>MOOVICAR</b>
                  <i className="bi bi-list" />
                </div>
                <div className="mv-phone-img">
                  <img src={heroCar} alt="" />
                </div>
                <h4>
                  Louez une voiture <em>en quelques clics</em>
                </h4>
                <div className="mv-phone-field">
                  <i className="bi bi-geo-alt" /> Adresse précise, gare, métro ...
                </div>
                <div className="mv-phone-row">
                  <div>
                    <small>Début</small>
                    {dayjs(selectedDates[0]).format('MM/DD/YYYY')}
                  </div>
                  <div>
                    <small>Fin</small>
                    {dayjs(selectedDates[1]).format('MM/DD/YYYY')}
                  </div>
                </div>
                <div className="mv-phone-row">
                  <div>{startTime}</div>
                  <div>{endTime}</div>
                </div>
                <div className="mv-phone-cta">Rechercher →</div>
                <div className="mv-phone-ai">
                  <i className="bi bi-stars" />
                  <span>
                    <b>Des recommandations</b>
                    adaptées à vos besoins
                  </span>
                </div>
              </div>
            </div>

            <p className="mv-script mv-script-bottom">
              Plus qu'une location,
              <br />
              une expérience !
            </p>
          </div>
        </div>
      </section>

      {/* =========================================================
          INTRO / BENEFITS
      ========================================================= */}
      <BodySection1 />
      {/* =========================================================
          COMMENT ÇA MARCHE
      ========================================================= */}
      <section className="mc-how-section">
        <div className="container mc-container">
          <div className="mc-section-heading">
            <span className="mc-section-kicker">L'EXPÉRIENCE MOOVICAR</span>

            <h2>
              De la recherche
              <span>à la route.</span>
            </h2>

            <p>
              Une expérience pensée pour supprimer les étapes inutiles et rendre la location aussi
              simple que possible.
            </p>
          </div>

          <div className="mc-process-grid">
            {[
              {
                number: '01',
                icon: 'bi-search',
                title: 'Recherchez',
                description:
                  'Indiquez votre localisation, vos dates et vos horaires. Trouvez rapidement les véhicules disponibles.',
              },
              {
                number: '02',
                icon: 'bi-calendar-check',
                title: 'Réservez',
                description:
                  'Choisissez votre véhicule et envoyez votre demande. Le propriétaire confirme la réservation.',
              },
              {
                number: '03',
                icon: 'bi-car-front-fill',
                title: 'Roulez',
                description:
                  "Récupérez le véhicule, effectuez l'état des lieux et profitez simplement de votre trajet.",
              },
            ].map((item) => (
              <div className="mc-process-card" key={item.number}>
                <div className="mc-process-top">
                  <span className="mc-process-number">{item.number}</span>

                  <div className="mc-process-icon">
                    <i className={`bi ${item.icon}`} />
                  </div>
                </div>

                <h3>{item.title}</h3>

                <p>{item.description}</p>

                <div className="mc-process-line" />
              </div>
            ))}
          </div>

          <div className="mc-process-links">
            <Link to="/comment-ca-marche">
              <i className="bi bi-arrow-up-right" />
              Guide conducteur / hôte
            </Link>

            <Link to="/regles-business">
              <i className="bi bi-arrow-up-right" />
              Commissions & règles
            </Link>

            <Link to="/etats-reservation">
              <i className="bi bi-arrow-up-right" />
              États de réservation
            </Link>
          </div>
        </div>
      </section>
      {/* =========================================================
          CATEGORIES
      ========================================================= */}
      <HeaderSection3 />
      {/* =========================================================
          ACTUALITÉS
      ========================================================= */}
      <HeaderSection2 />
      {/* =========================================================
          HOST SECTION
      ========================================================= */}
      <section className="mc-host-section">
        <div className="container mc-container">
          <div className="mc-host-card">
            <div className="mc-host-content">
              <span className="mc-section-kicker">POUR LES PROPRIÉTAIRES</span>

              <h2>
                Votre voiture peut
                <span>vous rapporter.</span>
              </h2>

              <p>
                Transformez les périodes où votre véhicule reste inutilisé en opportunité de
                revenus, simplement.
              </p>

              <div className="mc-host-highlight">
                <span>Jusqu'à</span>
                <strong>800 €</strong>
                <span>/ mois</span>
              </div>

              <p className="mc-host-note">Tous les trajets sont assurés par AXA.</p>

              <Link className="mc-host-button" to="/location-voiture">
                Louer ma voiture
                <i className="bi bi-arrow-up-right" />
              </Link>
            </div>

            <div className="mc-host-media">
              <div className="mc-host-orb" />

              <img src={car} alt="Louer son véhicule" />

              <div className="mc-floating-card">
                <span className="mc-floating-icon">
                  <i className="bi bi-graph-up-arrow" />
                </span>

                <div>
                  <small>REVENU POTENTIEL</small>
                  <strong>+800 € / mois</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* =========================================================
          SEO TAGS
      ========================================================= */}
      <section className="mc-seo-section">
        <div className="container mc-container">
          <span>Explorez Moovicar</span>

          <div className="mc-seo-tags">
            {[
              "Location voiture à l'aéroport",
              'Location voiture à la gare',
              'Marque et modèle',
              'Location voiture en France',
              'Location voiture en Allemagne',
            ].map((tag) => (
              <span key={tag}>
                {tag}
                <i className="bi bi-arrow-up-right" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
    MODALE SUCCÈS ENVOI DE DEMANDE
    ===================================================== */}
      {activeModal === 'reservation' && (
        <>
          <div
            onClick={closeModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reservationModalTitle"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(500px, calc(100vw - 32px))',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: '12px',
              zIndex: 1050,
              boxShadow: '0 15px 50px rgba(0,0,0,.25)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 id="reservationModalTitle" className="mb-0">
                Confirmation de réservation
              </h5>

              <button
                type="button"
                className="btn-close"
                onClick={closeModal}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3">
              <p className="text-muted small">
                Ajoutez un message pour le propriétaire (facultatif).
              </p>

              <textarea
                className="form-control"
                rows="4"
                placeholder="Entrez votre message ici…"
                onChange={(event) => setPersonnalisedMessage(event.target.value)}
                value={personnalisedMessage}
              />
            </div>

            <div className="d-flex gap-2 p-3 border-top">
              <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>
                Annuler
              </button>

              <button
                type="button"
                className="btn btn-primary text-white flex-grow-1"
                onClick={handleReserver}
                disabled={selectedVoitures.length === 0}
              >
                <i className="bi bi-lightning-fill me-1" />
                Confirmer
              </button>
            </div>
          </div>
        </>
      )}

      {activeModal === 'success' && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="successModalTitle"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(500px, calc(100vw - 32px))',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: '12px',
              zIndex: 1050,
              boxShadow: '0 15px 50px rgba(0,0,0,.25)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 id="successModalTitle" className="mb-0">
                Demande envoyée
              </h5>

              <button
                type="button"
                className="btn-close"
                onClick={handleSuccessOk}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3 text-center">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '48px' }} />

              <p className="mt-3 mb-1 fw-semibold">Votre message a été envoyé avec succès !</p>

              <p className="text-muted small mb-0">
                Vous serez notifié dès qu'un hôte accepte votre demande.
              </p>
            </div>

            <div className="p-3 border-top">
              <button
                type="button"
                className="btn btn-primary text-white w-100"
                onClick={handleSuccessOk}
              >
                OK
              </button>
            </div>
          </div>
        </>
      )}
    </main>
  );
}

export default Home;
